import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { SKU_ALLOCATION_CHANGE_LOG } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import { SelectDropdown } from '../../components/SelectDropdown';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';

const DATE_TYPES = {
  ALL_TIME: 'all_time',
  RANGE: 'range',
};

function toYMD(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getAllTimeRange() {
  // sku_allocation_change_log first started collecting entries on 2026-07-22.
  const start = new Date(2026, 6, 22);
  const end = new Date();
  return { startDate: toYMD(start), endDate: toYMD(end) };
}

const SOURCE_BADGE_CLASSES = {
  'Manual edit': 'bg-blue-50 text-blue-700 border-blue-200',
  'Bulk reset': 'bg-amber-50 text-amber-700 border-amber-200',
  'AI rebalance': 'bg-purple-50 text-purple-700 border-purple-200',
  Rollback: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SkuAllocationChangeLogReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 50;

  const defaultRange = getAllTimeRange();
  const [dateType, setDateType] = useUrlParam(
    'dateType',
    DATE_TYPES.ALL_TIME,
    (value) => Object.values(DATE_TYPES).includes(value),
  );
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultRange.startDate, defaultRange.endDate);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const columns = useMemo(
    () => [
      { key: 'date', label: 'Date', type: 'string' },
      { key: 'user', label: 'User', type: 'string' },
      { key: 'plan', label: 'Plan', type: 'string' },
      { key: 'sku', label: 'SKU', type: 'string' },
      { key: 'field', label: 'Field', type: 'string' },
      { key: 'from', label: 'From', type: 'string' },
      { key: 'to', label: 'To', type: 'string' },
      { key: 'source', label: 'Source', type: 'string' },
      { key: 'batch', label: 'Batch', type: 'string' },
      { key: 'note', label: 'Note', type: 'string' },
    ],
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(String(search || '').trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const effectiveRange = useMemo(() => {
    if (dateType === DATE_TYPES.ALL_TIME) return getAllTimeRange();
    return { startDate, endDate };
  }, [dateType, startDate, endDate]);

  const buildParams = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams({
        startDate: effectiveRange.startDate,
        endDate: effectiveRange.endDate,
        ...extra,
      });
      if (debouncedSearch) params.append('search', debouncedSearch);
      return params;
    },
    [effectiveRange.startDate, effectiveRange.endDate, debouncedSearch]
  );

  const fetchData = useCallback(async () => {
    if (!effectiveRange.startDate || !effectiveRange.endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = buildParams({ page: String(currentPage), pageSize: String(pageSize) });
      const res = await fetch(`/api/v1/reports/${SKU_ALLOCATION_CHANGE_LOG}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotalPages(json.totalPages || 1);
      setTotalRecords(json.totalRecords || 0);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [effectiveRange.startDate, effectiveRange.endDate, currentPage, buildParams, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const renderRows = () =>
    (data || []).map((row, idx) => (
      <tr key={`${row.date || 'date'}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.user || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.plan || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {row.sku ? (
            <a
              href={`${ROUTES.CATALOG}?search=${encodeURIComponent(row.sku)}`}
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.sku}
            </a>
          ) : (
            ''
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.field || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.from || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.to || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
              SOURCE_BADGE_CLASSES[row.source] || 'bg-gray-50 text-gray-700 border-gray-200'
            }`}
          >
            {row.source || ''}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-xs font-mono text-gray-500">{row.batch || ''}</td>
        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={row.note || ''}>
          {row.note || ''}
        </td>
      </tr>
    ));

  const filtersComponent = (
    <div className="contents">
      <input
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setCurrentPage(1);
        }}
        placeholder="Search SKU, plan, user, or source"
        className="w-40 sm:w-[240px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
      <SelectDropdown
        selectClassname="!bg-white"
        options={[
          { label: 'All Time', value: DATE_TYPES.ALL_TIME },
          { label: 'Date Range', value: DATE_TYPES.RANGE },
        ]}
        value={dateType}
        onChange={(value) => {
          setDateType(value);
          setCurrentPage(1);
        }}
      />
      {dateType === DATE_TYPES.RANGE && (
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={({ startDate: newStartDate, endDate: newEndDate }) => {
            setDateRange({ startDate: newStartDate, endDate: newEndDate });
            setCurrentPage(1);
          }}
          label="Date Range"
        />
      )}
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={columns}
      filename={SKU_ALLOCATION_CHANGE_LOG}
      onExport={async () => {
        const params = buildParams();
        const res = await fetch(`/api/v1/reports/${SKU_ALLOCATION_CHANGE_LOG}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${SKU_ALLOCATION_CHANGE_LOG}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalRecords)} of{' '}
          {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <ReportTemplate
      reportId={SKU_ALLOCATION_CHANGE_LOG}
      description="Every SKU Allocation input change (Override %, New Product Override %, approvals) with who changed it, when, the old → new values, the change source, and batch grouping"
      breadcrumbs={breadcrumbs}
      onBack={handleBackToReports}
      marginFromSidebar={marginFromSidebar}
      isLoading={isLoading}
      error={error}
      filters={filtersComponent}
      exportButton={exportButton}
      data={data}
      columns={columns}
      pagination={paginationComponent}
      emptyMessage="No SKU Allocation changes found for the selected filters."
    >
      {renderRows()}
    </ReportTemplate>
  );
};

export default SkuAllocationChangeLogReport;
